import every from 'lodash/every'
import includes from 'lodash/includes'
import uniq from 'lodash/uniq'
import {exhaustive} from '@freckle/exhaustive'
import {fromMaybe, maybe, mapMaybes} from '@freckle/maybe'
import {parseQueryParamTextArray} from '@freckle/educator-entities/ts/common/helpers/routers/query-params'
import StorageHelper from '@freckle/educator-entities/ts/common/helpers/storage-helper'
import type {IdentityProviderT} from '@freckle/educator-entities/ts/roster/identity-provider'
import {
  parse as parseIdp,
  IdentityProvider
} from '@freckle/educator-entities/ts/roster/identity-provider'
import type {SchoolAttrs} from '@freckle/educator-entities/ts/roster/models/school'
import type {SummaryForEntityT, SummaryForEntityTagT} from './../components/source-dropdown/utils'
import {getPossibleSources, getInitialSelection} from './../components/source-dropdown/utils'

export type IdentityProviderOrNoneT = IdentityProviderT | 'none'

export const NO_IDENTITY_PROVIDER_VALUE: IdentityProviderOrNoneT = 'none'

const parseIdentityProviderOrNone = (value: string): IdentityProviderOrNoneT | undefined | null =>
  value === 'none' ? 'none' : parseIdp(value)

const IDENTITY_PROVIDER_OR_NONE_VALUES: Array<IdentityProviderOrNoneT> =
  IdentityProvider.all().concat([NO_IDENTITY_PROVIDER_VALUE] as unknown as IdentityProviderT[])

export const providersOrNoneToQueryFilter = (
  identityProviders: Array<string>
): Record<string, unknown> =>
  identityProviders.length === 0
    ? // This is an intentional contradiction. Nothing selected should yield
      // nothing.
      //
      // Note that the higher-level dropdown components prevent this. If
      // everything is de-selected, they force an "all" selection.
      {
        'identity-provider[in]': NO_IDENTITY_PROVIDER_VALUE,
        'identity-provider[notin]': NO_IDENTITY_PROVIDER_VALUE
      }
    : every(IDENTITY_PROVIDER_OR_NONE_VALUES, (value: IdentityProviderOrNoneT) =>
          includes(identityProviders, value)
        )
      ? // All providers given, so omit filter
        {}
      : {'identity-provider': identityProviders.join(',')}

const storageKey = (summaryTag: SummaryForEntityTagT) => `source-filters-${summaryTag}`

const getSourceFilterFromLocalStorage = (summaryTag: SummaryForEntityTagT): Array<string> =>
  fromMaybe(() => [], StorageHelper.getLocalStorageProperty(storageKey(summaryTag)))

export const setSourceFilterInLocalStorage = (
  summaryTag: SummaryForEntityTagT,
  sourceFilter: Array<IdentityProviderOrNoneT>
) => StorageHelper.setLocalStorageProperty(storageKey(summaryTag), sourceFilter)

type AllOptionsSource = {tag: 'summary'} | {tag: 'all-schools'; allSchools: SchoolAttrs[]}

export const parseDefaultIdpsFromQueryParam = (
  mTextKey: string | undefined | null,
  summary: SummaryForEntityT,
  allOptionsSource: AllOptionsSource
) => {
  const initSources = mapMaybes(
    maybe(
      () => getSourceFilterFromLocalStorage(summary.tag),
      textKeyArray => parseQueryParamTextArray(textKeyArray),
      mTextKey
    ),
    parseIdentityProviderOrNone
  )

  return getInitialSelection(initSources, possibleSources(summary, allOptionsSource), summary)
}

const possibleSources = (summary: SummaryForEntityT, allOptionsSource: AllOptionsSource) => {
  switch (allOptionsSource.tag) {
    case 'summary':
      return getPossibleSources(summary)
    case 'all-schools':
      return uniq(
        allOptionsSource.allSchools.map(school => {
          switch (school.activeIdp) {
            case 'class-link':
            case 'clever':
            case 'csv':
            case 'renaissance':
              return school.activeIdp
            case null:
            case undefined:
              return 'none'
            default:
              return exhaustive(school.activeIdp)
          }
        })
      )
    default:
      return exhaustive(allOptionsSource)
  }
}
